import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSpace } from '../hooks/useSpaces';
import { useAuth } from '../contexts/AuthContext';

export const SpacePage: React.FC = () => {
  const { spaceId } = useParams<{ spaceId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const {
    space,
    participants,
    messages,
    loading,
    error,
    isUserInSpace,
    isUserHost,
    isUserSpeaker,
    currentParticipantCount,
    sendMessage
  } = useSpace(spaceId || null);

  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isJoining, setIsJoining] = useState(false);

  // Gestion de rejoindre le Space
  const handleJoinSpace = async () => {
    if (!spaceId || isJoining) return;
    
    setIsJoining(true);
    try {
      // TODO: Implémenter la logique pour rejoindre un Space
      // Pour l'instant, on simule un succès
      console.log('Joining space:', spaceId);
      // Après avoir rejoint avec succès, on peut recharger la page
      window.location.reload();
    } catch (error) {
      console.error('Erreur rejoindre Space:', error);
    } finally {
      setIsJoining(false);
    }
  };

  // Rediriger si pas d'ID de Space
  useEffect(() => {
    if (!spaceId) {
      navigate('/');
      return;
    }
  }, [spaceId, navigate]);

  // Gestion d'envoi de message
  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim() || isSending) return;

    setIsSending(true);
    const success = await sendMessage(message.trim());
    if (success) {
      setMessage('');
    }
    setIsSending(false);
  };

  // États de chargement et d'erreur
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#C9A96E] mx-auto mb-4"></div>
          <p className="text-white">Chargement du Space...</p>
        </div>
      </div>
    );
  }

  if (error || !space) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h1 className="text-white text-2xl mb-2">Space introuvable</h1>
          <p className="text-gray-400 mb-4">
            {error || 'Ce Space n\'existe pas ou vous n\'y avez pas accès.'}
          </p>
          <button
            onClick={() => navigate('/')}
            className="bg-[#C9A96E] text-black px-6 py-2 rounded-lg hover:bg-[#B8965A] transition-colors"
          >
            Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Header du Space */}
      <div className="bg-[#1a1a1a] border-b border-gray-800 p-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-2">
            <button
              onClick={() => navigate(-1)}
              className="text-[#C9A96E] hover:text-[#B8965A] transition-colors"
            >
              ← Retour
            </button>
            <div className="flex items-center space-x-4">
              {space.isLive && (
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                  <span className="text-red-500 font-semibold">EN DIRECT</span>
                </div>
              )}
              <div className="text-gray-400">
                {currentParticipantCount} participant{currentParticipantCount > 1 ? 's' : ''}
              </div>
            </div>
          </div>
          
          <h1 className="text-2xl font-bold mb-2">{space.title}</h1>
          
          {space.description && (
            <p className="text-gray-400 mb-3">{space.description}</p>
          )}
          
          <div className="flex items-center space-x-4 text-sm">
            {isUserHost && (
              <span className="bg-[#C9A96E] text-black px-2 py-1 rounded text-xs font-semibold">
                HOST
              </span>
            )}
            {isUserSpeaker && !isUserHost && (
              <span className="bg-blue-600 text-white px-2 py-1 rounded text-xs font-semibold">
                SPEAKER
              </span>
            )}
            <span className="text-gray-400">
              Max: {space.maxParticipants} participants
            </span>
            {space.isPublic && (
              <span className="text-green-400 text-xs">Public</span>
            )}
          </div>
        </div>
      </div>

      {/* Contenu principal */}
      <div className="max-w-4xl mx-auto p-4">
        {!isUserInSpace ? (
          // Écran pour rejoindre le Space
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🎙️</div>
            <h2 className="text-xl mb-4">Vous n'êtes pas dans ce Space</h2>
            <p className="text-gray-400 mb-6">
              Rejoignez la conversation pour participer !
            </p>
            <button
              className="bg-[#C9A96E] text-black px-6 py-3 rounded-lg hover:bg-[#B8965A] transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleJoinSpace}
              disabled={isJoining}
            >
              {isJoining ? 'Connexion...' : 'Rejoindre le Space'}
            </button>
          </div>
        ) : (
          // Interface du Space pour les participants
          <div className="space-y-6">
            {/* Zone de chat */}
            <div className="bg-[#1a1a1a] rounded-lg p-4">
              <h3 className="text-lg font-semibold mb-4 flex items-center">
                💬 Chat du Space
                <span className="text-sm text-gray-400 ml-2">
                  ({messages.length} message{messages.length > 1 ? 's' : ''})
                </span>
              </h3>
              
              {/* Messages */}
              <div className="space-y-3 mb-4 max-h-64 overflow-y-auto">
                {messages.length === 0 ? (
                  <p className="text-gray-400 text-center py-4">
                    Aucun message pour le moment. Soyez le premier à parler !
                  </p>
                ) : (
                  messages.map((msg) => (
                    <div key={msg.id} className="flex space-x-3">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <span className="font-semibold text-[#C9A96E]">
                            {msg.authorId === user?.walletAddress ? 'Vous' : `${msg.authorId.slice(0, 8)}...`}
                          </span>
                          <span className="text-xs text-gray-400">
                            {msg.createdAt instanceof Date ? msg.createdAt.toLocaleTimeString() : 'Maintenant'}
                          </span>
                        </div>
                        <p className="text-gray-300">{msg.content}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Formulaire d'envoi de message */}
              <form onSubmit={handleSendMessage} className="flex space-x-3">
                <input
                  type="text"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Tapez votre message..."
                  className="flex-1 bg-[#2a2a2a] border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-400 focus:outline-none focus:border-[#C9A96E]"
                  maxLength={500}
                  disabled={isSending}
                />
                <button
                  type="submit"
                  disabled={!message.trim() || isSending}
                  className="bg-[#C9A96E] text-black px-6 py-2 rounded-lg hover:bg-[#B8965A] transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
                >
                  {isSending ? '...' : 'Envoyer'}
                </button>
              </form>
            </div>

            {/* Contrôles audio */}
            <div className="bg-[#1a1a1a] rounded-lg p-4">
              <h3 className="text-lg font-semibold mb-4">🎤 Contrôles Audio</h3>
              <div className="text-center py-8">
                <div className="text-4xl mb-4">🚧</div>
                <p className="text-gray-400">
                  Les fonctionnalités audio seront bientôt disponibles !
                </p>
              </div>
            </div>

            {/* Participants */}
            <div className="bg-[#1a1a1a] rounded-lg p-4">
              <h3 className="text-lg font-semibold mb-4">
                👥 Participants ({currentParticipantCount})
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                {space.participants.map((participantId) => (
                  <div key={participantId} className="flex items-center space-x-3 p-2 bg-[#2a2a2a] rounded-lg">
                    <div className="w-8 h-8 bg-[#C9A96E] rounded-full flex items-center justify-center text-black font-semibold">
                      {participantId.slice(0, 2).toUpperCase()}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium">
                        {participantId === user?.walletAddress ? 'Vous' : `${participantId.slice(0, 8)}...`}
                      </p>
                      <div className="flex space-x-1">
                        {space.hostId === participantId && (
                          <span className="text-xs bg-[#C9A96E] text-black px-1 rounded">HOST</span>
                        )}
                        {space.speakers && space.speakers.includes(participantId) && space.hostId !== participantId && (
                          <span className="text-xs bg-blue-600 text-white px-1 rounded">SPEAKER</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
